import { convexQuery } from '@convex-dev/react-query';
import { api } from '@repo/backend/_generated/api';
import type { Role } from '@repo/backend/schema';
import { Popover, PopoverContent, PopoverTrigger } from '@repo/ui/components/ui/popover';
import { cn } from '@repo/ui/lib/utils';
import { useQuery } from '@tanstack/react-query';
import { Check, Crown, Eye, Shield, UserRound } from 'lucide-react';
import { useViewAsMode, type ViewAsMode } from '@/hooks/use-view-as-mode';

const ROLE_CONFIG: Record<
    Role,
    {
        label: string;
        icon: typeof Shield;
        accentColor: string;
        glowColor: string;
        iconBg: string;
    }
> = {
    super_user: {
        label: 'Super User',
        icon: Crown,
        accentColor: 'text-amber-400',
        glowColor: 'shadow-[0_0_20px_rgba(251,191,36,0.4),inset_0px_1px_0px_rgba(251,191,36,0.3)]',
        iconBg: 'bg-gradient-to-br from-amber-400 to-orange-500',
    },
    admin: {
        label: 'Admin',
        icon: Shield,
        accentColor: 'text-violet-400',
        glowColor: 'shadow-[0_0_20px_rgba(167,139,250,0.4),inset_0px_1px_0px_rgba(167,139,250,0.3)]',
        iconBg: 'bg-gradient-to-br from-violet-400 to-purple-600',
    },
    moderator: {
        label: 'Moderator',
        icon: Eye,
        accentColor: 'text-cyan-400',
        glowColor: 'shadow-[0_0_20px_rgba(34,211,238,0.4),inset_0px_1px_0px_rgba(34,211,238,0.3)]',
        iconBg: 'bg-gradient-to-br from-cyan-400 to-blue-500',
    },
};

const VIEW_AS_BRAND_CONFIG = {
    label: 'Viewing as Brand',
    icon: UserRound,
    accentColor: 'text-sky-400',
    glowColor: 'shadow-[0_0_20px_rgba(56,189,248,0.4),inset_0px_1px_0px_rgba(56,189,248,0.3)]',
    iconBg: 'bg-gradient-to-br from-sky-400 to-blue-600',
};

const ROLE_PRIORITY: Role[] = ['super_user', 'admin', 'moderator'];

export const RoleBadge = () => {
    const { data: currentUser } = useQuery(convexQuery(api.users.current, {}));
    const { mode, setMode } = useViewAsMode();

    // Get the highest priority real role
    const highestRole = ROLE_PRIORITY.find((role) => currentUser?.roles?.includes(role));
    if (!highestRole) {
        return null;
    }

    const isSuperUser = highestRole === 'super_user';

    // Display config swaps to "Viewing as Brand" indicator when override is on
    const displayConfig = mode === 'brand' ? VIEW_AS_BRAND_CONFIG : ROLE_CONFIG[highestRole];
    const Icon = displayConfig.icon;

    const badgeContent = (
        <>
            <div className={cn('flex items-center justify-center', 'size-7 rounded-full', 'shadow-[0_2px_8px_rgba(0,0,0,0.3)]', displayConfig.iconBg)}>
                <Icon className='size-4 text-white drop-shadow-sm' aria-hidden='true' />
            </div>
            <span className={cn('font-semibold text-sm tracking-wide', displayConfig.accentColor)}>{displayConfig.label}</span>
            <span
                className={cn('absolute inset-0 rounded-full', 'animate-pulse opacity-30', 'border border-current', displayConfig.accentColor)}
                style={{ animationDuration: '3s' }}
            />
        </>
    );

    const wrapperClassName = cn(
        'fixed right-4 bottom-4 z-50',
        'flex items-center gap-2.5 py-1.5 pr-3.5 pl-1.5',
        'rounded-full',
        'border-2 border-white/20',
        'bg-linear-to-b from-[#2a2a2a] to-[#141414]',
        'shadow-[inset_0px_2px_6px_rgba(255,255,255,0.1),inset_0px_-4px_6px_rgba(0,0,0,0.4)]',
        'backdrop-blur-xl',
        'select-none',
        'fade-in slide-in-from-bottom-3 animate-in duration-500',
        displayConfig.glowColor
    );

    // Non-super users see the static badge as before
    if (!isSuperUser) {
        return (
            <div className={wrapperClassName} role='status' aria-label={`Your role: ${displayConfig.label}`}>
                {badgeContent}
            </div>
        );
    }

    // Super users get an interactive popover for view-as control
    return (
        <Popover>
            <PopoverTrigger
                render={
                    <button type='button' className={cn(wrapperClassName, 'cursor-pointer transition-transform hover:scale-105')} aria-label='Open mod menu'>
                        {badgeContent}
                    </button>
                }
            />
            <PopoverContent side='top' align='end' sideOffset={8} className='w-64 gap-2'>
                <div className='space-y-1'>
                    <p className='font-semibold text-foreground text-sm'>Mod Menu</p>
                    <p className='text-muted-foreground text-xs'>Override your UI role for QA. Backend permissions are unchanged.</p>
                </div>
                <div className='flex flex-col gap-1'>
                    <ViewAsOption label='Super User (default)' description='Full admin UI' active={mode === 'default'} onClick={() => setMode('default')} />
                    <ViewAsOption label='Brand Owner' description='Hide admin gates' active={mode === 'brand'} onClick={() => setMode('brand')} />
                </div>
            </PopoverContent>
        </Popover>
    );
};

function ViewAsOption({ label, description, active, onClick }: { label: string; description: string; active: boolean; onClick: () => void }) {
    return (
        <button
            type='button'
            onClick={onClick}
            className={cn(
                'flex items-start justify-between gap-2 rounded-md border px-3 py-2 text-left transition-colors',
                active ? 'border-primary/40 bg-primary/10' : 'border-transparent hover:bg-white/5'
            )}>
            <div className='min-w-0'>
                <p className={cn('font-medium text-sm', active ? 'text-primary' : 'text-foreground')}>{label}</p>
                <p className='text-muted-foreground text-xs'>{description}</p>
            </div>
            {active && <Check className='size-4 shrink-0 text-primary' />}
        </button>
    );
}

// Re-export to make ViewAsMode available alongside the badge if needed elsewhere.
export type { ViewAsMode };
